import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { cartApi, cartTokenStorage } from '@/lib/api'

interface CartItem {
  id: number
  product_id: number
  product_name: string
  quantity: number
  price: number
  image_url?: string
}

interface Cart {
  items: CartItem[]
  subtotal: number
  total: number
  discount_amount?: number
  discount_code?: string
  cart_token?: string
}

export const Route = createFileRoute('/cart')({
  component: CartPage,
})

function CartPage() {
  const [cart, setCart] = useState<Cart | null>(null)
  const [loading, setLoading] = useState(true)
  const [discountCode, setDiscountCode] = useState('')
  const [applyingDiscount, setApplyingDiscount] = useState(false)
  const [discountError, setDiscountError] = useState('')
  const [discountSuccess, setDiscountSuccess] = useState('')
  const currencySymbol = '₹'

  useEffect(() => {
    loadCart()
  }, [])

  const loadCart = async () => {
    try {
      const data = (await cartApi.get()) as Cart
      setCart(data)

      // Store cart token if returned
      if (data.cart_token) {
        cartTokenStorage.set(data.cart_token)
      }

      // Update discount code field if cart has one applied
      if (data.discount_code) {
        setDiscountCode(data.discount_code)
        setDiscountSuccess('Discount applied!')
      }
    } catch (error) {
      console.error('Failed to load cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateQuantity = async (itemId: number, quantity: number) => {
    try {
      await cartApi.updateItem(itemId, quantity)
      await loadCart()
    } catch (error) {
      console.error('Failed to update:', error)
    }
  }

  const removeItem = async (itemId: number) => {
    try {
      await cartApi.removeItem(itemId)
      await loadCart()
    } catch (error) {
      console.error('Failed to remove:', error)
    }
  }

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code')
      return
    }

    setApplyingDiscount(true)
    setDiscountError('')
    setDiscountSuccess('')

    try {
      const cartToken = cartTokenStorage.get()
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/v1/cart/apply-discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(cartToken ? { 'X-Cart-Token': cartToken } : {}),
        },
        body: JSON.stringify({ code: discountCode.toUpperCase() }),
      })

      if (response.ok) {
        setDiscountSuccess('Discount applied!')
        await loadCart()
      } else {
        const data = await response.json()
        setDiscountError(data.detail || 'Invalid discount code')
      }
    } catch (err) {
      setDiscountError('Failed to apply discount. Please try again.')
    } finally {
      setApplyingDiscount(false)
    }
  }

  const removeDiscountCode = async () => {
    try {
      const cartToken = cartTokenStorage.get()
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/v1/cart/remove-discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(cartToken ? { 'X-Cart-Token': cartToken } : {}),
        },
      })

      if (response.ok) {
        setDiscountCode('')
        setDiscountSuccess('')
        setDiscountError('')
        await loadCart()
      }
    } catch (err) {
      console.error('Failed to remove discount:', err)
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-4">Your Cart is Empty</h1>
          <p className="text-gray-600 mb-8">Add some products to get started.</p>
          <Link
            to="/products"
            search={{}}
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    )
  }

  const hasDiscount = cart.discount_amount && cart.discount_amount > 0

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border divide-y">
            {cart.items.map((item) => (
              <div key={item.id} className="p-4 flex gap-4">
                <Link to="/products/$slug" params={{ slug: String(item.product_id) }} className="flex-shrink-0">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.product_name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No image</span>
                    </div>
                  )}
                </Link>

                <div className="flex-1 min-w-0">
                  <Link to="/products/$slug" params={{ slug: String(item.product_id) }} className="block">
                    <h3 className="font-semibold hover:text-blue-600 truncate">{item.product_name}</h3>
                  </Link>
                  <p className="text-gray-600">{currencySymbol}{item.price.toLocaleString()}</p>

                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex items-center border rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors"
                      >
                        -
                      </button>
                      <span className="w-10 text-center text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="ml-2 text-red-600 text-sm hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="font-semibold text-right">
                  {currencySymbol}{(item.price * item.quantity).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {/* Continue shopping link */}
          <Link to="/products" search={{}} className="inline-flex items-center gap-2 mt-4 text-gray-600 hover:text-gray-900">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Continue Shopping
          </Link>
        </div>

        {/* Order Summary */}
        <div>
          <div className="bg-white rounded-lg border p-6 sticky top-24">
            <h2 className="font-bold text-lg mb-4">Order Summary</h2>

            {/* Discount Code Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Discount Code</label>
              {cart.discount_code ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium text-green-800">{cart.discount_code}</span>
                  </div>
                  <button
                    onClick={removeDiscountCode}
                    className="text-sm text-green-700 hover:text-green-900"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value.toUpperCase())
                      setDiscountError('')
                    }}
                    placeholder="Enter code"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  <button
                    onClick={applyDiscountCode}
                    disabled={applyingDiscount || !discountCode}
                    className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                  >
                    {applyingDiscount ? '...' : 'Apply'}
                  </button>
                </div>
              )}

              {discountError && <p className="text-sm text-red-600 mt-2">{discountError}</p>}
              {discountSuccess && !cart.discount_code && (
                <p className="text-sm text-green-600 mt-2">{discountSuccess}</p>
              )}

              {/* Available codes hint in dev */}
              {import.meta.env.DEV && (
                <div className="mt-2 text-xs text-gray-500">
                  Try: WELCOME10, LUXURY20, DIWALI25
                </div>
              )}
            </div>

            <div className="space-y-2 mb-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal ({cart.items.length} items)</span>
                <span>{currencySymbol}{cart.subtotal.toLocaleString()}</span>
              </div>

              {hasDiscount && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-{currencySymbol}{cart.discount_amount!.toLocaleString()}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="text-gray-500">Calculated at checkout</span>
              </div>
            </div>

            <div className="border-t pt-4 mb-6">
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>{currencySymbol}{cart.total.toLocaleString()}</span>
              </div>
              {hasDiscount && (
                <p className="text-sm text-green-600 mt-1">
                  You save {currencySymbol}{cart.discount_amount!.toLocaleString()}!
                </p>
              )}
            </div>

            <Link
              to="/checkout"
              className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Proceed to Checkout
            </Link>

            {/* Security badges */}
            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  Secure Checkout
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  Safe Payment
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
